import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Receipt, Banknote, CreditCard, AlertTriangle } from 'lucide-react';
import { EmailTicketForm } from '../components/EmailTicketForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicTicket = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/ticket-public/${token}`);
        if (!res.ok) throw new Error(res.status === 404 ? 'Ticket no encontrado o caducado' : 'Error al cargar el ticket');
        const j = await res.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_URL}/api/ticket-public/${token}/pdf`);
      if (!res.ok) throw new Error('PDF no disponible');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-pont10-${token.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) {
      alert(e.message);
    } finally {
      setDownloading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="public-ticket-error">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <AlertTriangle size={40} className="mx-auto text-amber-500 mb-3" />
          <p className="text-gray-800 font-semibold">{error}</p>
          <p className="text-gray-500 text-sm mt-2">Los tickets se conservan 30 días.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Cargando ticket...</div>
      </div>
    );
  }

  const { sale, settings } = data;
  const items = sale.items || [];
  const payLabel = sale.payment_method === 'efectivo' ? 'Efectivo' : 'Tarjeta';
  const PayIcon = sale.payment_method === 'efectivo' ? Banknote : CreditCard;

  let dateStr = '', timeStr = '';
  try {
    const dt = new Date(sale.created_at || sale.session_started_at);
    dateStr = dt.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    timeStr = dt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  } catch { /* noop */ }

  const ticketNum = (sale.id || '').slice(0, 8).toUpperCase();
  const legalCity = [settings.postal_code, settings.city].filter(Boolean).join(' ') || settings.address_line2;

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-3" data-testid="public-ticket-page">
      <div className="max-w-sm mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-900 text-white px-6 py-5 text-center">
          <h1 className="text-2xl font-bold tracking-wider">{settings.business_name || 'PONT 10'}</h1>
          {settings.business_subtitle && <p className="text-gray-400 text-xs mt-1">{settings.business_subtitle}</p>}
        </div>

        {/* Legal data */}
        <div className="px-5 pt-4 pb-3 text-center text-xs text-gray-500 space-y-0.5">
          {settings.nif_cif && <p>NIF/CIF: {settings.nif_cif}</p>}
          {settings.address_line1 && <p>{settings.address_line1}</p>}
          {legalCity && <p>{legalCity}</p>}
          {settings.phone && <p>Tel: {settings.phone}</p>}
          {settings.email && <p>{settings.email}</p>}
        </div>

        <div className="mx-5 border-t border-dashed border-gray-300" />

        {/* Ticket meta */}
        <div className="px-5 py-3 flex items-center justify-between text-sm">
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Ticket</p>
            <p className="font-mono font-semibold">{ticketNum}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Mesa</p>
            <p className="font-bold text-lg">{sale.mesa_numero ?? '-'}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-gray-400">{dateStr}</p>
            <p className="text-xs font-medium">{timeStr}</p>
          </div>
        </div>

        {/* Items */}
        <div className="px-5">
          <div className="border-t-2 border-gray-900 pt-2">
            <div className="grid grid-cols-12 gap-1 mb-1">
              <span className="col-span-7 text-[10px] text-gray-400 uppercase font-medium">Producto</span>
              <span className="col-span-2 text-[10px] text-gray-400 uppercase font-medium text-center">Uds</span>
              <span className="col-span-3 text-[10px] text-gray-400 uppercase font-medium text-right">Importe</span>
            </div>
            {items.map((item, idx) => {
              let name = item.product_name || item.name || 'Producto';
              if (typeof name === 'object') name = name.es || Object.values(name)[0] || 'Producto';
              const qty = item.quantity || 1;
              const price = item.price || 0;
              return (
                <div key={idx} className="grid grid-cols-12 gap-1 py-1.5 border-b border-gray-100">
                  <div className="col-span-7">
                    <p className="text-sm text-gray-800 leading-tight">{name}</p>
                    {item.notes && <p className="text-[10px] text-gray-400">{item.notes}</p>}
                  </div>
                  <p className="col-span-2 text-sm text-gray-500 text-center">{qty}</p>
                  <p className="col-span-3 text-sm font-semibold text-right">{(qty * price).toFixed(2)}€</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Total + Pago */}
        <div className="px-5 py-3">
          <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
            <span className="text-xs text-gray-500 uppercase font-semibold">Total</span>
            <span className="text-3xl font-extrabold text-green-600">{(sale.total_amount || 0).toFixed(2)}€</span>
          </div>
          <div className="flex justify-center mt-3">
            <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold ${
              sale.payment_method === 'efectivo' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
            }`}>
              <PayIcon size={14} /> {payLabel}
            </span>
          </div>
        </div>

        <div className="mx-5 border-t border-dashed border-gray-300" />

        {/* Download button */}
        <div className="px-5 py-4">
          <button
            onClick={handleDownload}
            disabled={downloading}
            data-testid="public-ticket-download-btn"
            className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
          >
            <Download size={18} />
            {downloading ? 'Preparando PDF...' : 'Descargar PDF'}
          </button>
          <div className="mt-4">
            <EmailTicketForm token={token} label="Enviarme el ticket por email" idPrefix="public-ticket" />
          </div>
          <p className="text-center text-[10px] text-gray-400 mt-3">
            Este documento es un ticket simplificado. Se conserva 30 días.
          </p>
        </div>

        {/* Footer */}
        {(settings.footer_message1 || settings.footer_message2) && (
          <div className="text-center pb-5 px-5 space-y-0.5">
            {settings.footer_message1 && <p className="text-xs text-gray-500">{settings.footer_message1}</p>}
            {settings.footer_message2 && <p className="text-[11px] text-gray-400">{settings.footer_message2}</p>}
          </div>
        )}
      </div>
      <div className="text-center mt-4 flex items-center justify-center gap-1 text-xs text-gray-400">
        <Receipt size={12} /> pont10.cat
      </div>
    </div>
  );
};

export default PublicTicket;
